import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AiFillCheckCircle } from '@react-icons/all-files/ai/AiFillCheckCircle';
import { CloseSquareFilled } from '@ant-design/icons';
import { Card, Collapse, Tag } from 'antd';
import {
  PROPERTY_TYPE_TITLES,
  PROPERTY_TYPES,
} from '@zorba-shared/core/Constants/deal';
import { getPriceText } from '@zorba-shared/core/Services/textService';
import { useSearchParams } from '../../utils';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import ContentHeader from '../../components/ContentHeader';
import { PlacesAutoComplete, ZorbaSelect } from '../../components/forms';
import { showErrorToast } from '../../services/notificationsService';
import { postRequest } from '../../services/requestService';
import { buildBuyerPageUrl } from '../../services/urlBuilderService';
import { filterBuyers } from './service';

import './index.scss';

const DEFAULT_FILTERS = {
  mlsDeals: undefined,
  preDeals: undefined,
  pool: undefined,
  propertyTypes: [],
};

const BuyboxCheckerPage = () => {
  useDocumentTitle('Buybox Checker');
  const [searchParams] = useSearchParams();
  const queryAddress = searchParams.get('query');
  const [buyers, setMatchingBuyers] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [hasSearched, setHasSearched] = useState(false);

  const fetchBuyers = async (selectedAddress) => {
    setIsLoading(true);
    setHasSearched(true);
    setMatchingBuyers({});
    try {
      const response = await postRequest('/backoffice/buybox-checker/search', {
        address: selectedAddress,
        ...filters,
      });
      setMatchingBuyers(response?.data?.buyers || {});
    } catch {
      showErrorToast('Fetching matching buyers failed!');
    }
    setIsLoading(false);
  };

  const onPlacesChange = async (newAddress) => {
    if (!newAddress) return;
    await fetchBuyers(newAddress);
  };

  const onFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
  };

  const formatYearBuiltRange = (yearBuiltRange) => {
    const [minYear, maxYear] = yearBuiltRange;

    if (minYear && maxYear) {
      return `${minYear}-${maxYear}`;
    }

    if (minYear) {
      return `${minYear}+`;
    }

    if (maxYear) {
      return `Max ${maxYear}`;
    }

    return 'N/A';
  };

  const filteredBuyers = filterBuyers(buyers, filters);
  return (
    <>
      <ContentHeader title="Buybox Checker" />
      <div className="matching-buybox-container">
        <div className="search-container">
          <PlacesAutoComplete
            title={null}
            disabled={isLoading}
            allowFullAddress={false}
            placeholder="I'm looking for buyers in.."
            onChange={(value) => onPlacesChange(value)}
            initialInputText={queryAddress}
            clearInputOnSelect={false}
            clearInputOnBlur={false}
            allowClear={{ clearIcon: <CloseSquareFilled /> }}
          />
          {Object.entries(buyers).length > 0 && (
            <>
              <ZorbaSelect
                placeholder="Buying MLS deals"
                options={[
                  ...(filters.mlsDeals !== undefined
                    ? [{ label: 'All', value: undefined }]
                    : []),
                  { label: 'Yes', value: true },
                  { label: 'No', value: false },
                ]}
                value={filters.mlsDeals}
                onChange={(value) => onFilterChange('mlsDeals', value)}
                className="select"
              />
              <ZorbaSelect
                placeholder="Buying pre-contract"
                options={[
                  ...(filters.preDeals !== undefined
                    ? [{ label: 'All', value: undefined }]
                    : []),
                  { label: 'Yes', value: true },
                  { label: 'No', value: false },
                ]}
                value={filters.preDeals}
                onChange={(value) => onFilterChange('preDeals', value)}
                className="select"
              />
              <ZorbaSelect
                placeholder="Buy with pool"
                options={[
                  ...(filters.pool !== undefined
                    ? [{ label: 'All', value: undefined }]
                    : []),
                  { label: 'Yes', value: 'any' },
                  { label: 'No', value: 'no_pool' },
                ]}
                value={filters.pool}
                onChange={(value) => onFilterChange('pool', value)}
                className="select"
              />
              <ZorbaSelect
                placeholder="Property types"
                mode="multiple"
                options={PROPERTY_TYPES.map((type) => ({
                  label: PROPERTY_TYPE_TITLES[type],
                  value: type,
                }))}
                value={filters.propertyTypes}
                onChange={(value) => onFilterChange('propertyTypes', value)}
                className="select"
              />
            </>
          )}
        </div>
        {!hasSearched && (
          <span style={{ color: '#3F434A' }}>Type city, state, or zipcode</span>
        )}
        {isLoading && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '25px',
            }}
          >
            {`Searching through all of our buyer's buyboxes...`}
          </div>
        )}
        {!isLoading &&
          hasSearched &&
          Object.entries(filteredBuyers).length === 0 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: '25px',
                color: '#3F434A',
              }}
            >
              No matching buyers found for the selected filters
            </div>
          )}
        <div className="buyers-container" style={{ marginTop: '20px' }}>
          {Object.entries(filteredBuyers).map(([buyerId, buyer]) => (
            <Card key={buyerId} style={{ marginBottom: '10px' }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: '10px',
                }}
              >
                <h3
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    columnGap: '5px',
                  }}
                >
                  <AiFillCheckCircle
                    style={{ color: '#49e387', fontSize: 35 }}
                  />
                  <Link
                    style={{
                      color: 'black',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                    }}
                    to={buildBuyerPageUrl({
                      userDetails: { _id: buyer.buyer },
                    })}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {buyer.name}
                  </Link>
                </h3>
                <span>{`Matching buybox title: ${buyer.matchedBuyBox.title}`}</span>
                <span>{`Buy MLS deals: ${
                  buyer.organization.buyerConfig.allowMlsDeals ? 'Yes' : 'No'
                }`}</span>
                <span>{`Buying pre-contact: ${
                  buyer.organization.buyersAppFeatureFlags.preDeals.isActive
                    ? 'Yes'
                    : 'No'
                }`}</span>
                <span>{`Property types: ${buyer.matchedBuyBox.propertyTypes
                  .map((type) => PROPERTY_TYPE_TITLES[type])
                  .join(', ')}`}</span>
                <span>{`Price range: ${
                  buyer.matchedBuyBox.priceRange[0]
                    ? getPriceText(buyer.matchedBuyBox.priceRange[0])
                    : 'N/D'
                } - ${
                  buyer.matchedBuyBox.priceRange[1]
                    ? getPriceText(buyer.matchedBuyBox.priceRange[1])
                    : 'N/D'
                }`}</span>
                <span>{`Beds: ${buyer.matchedBuyBox.bedroomRange[0]} - ${buyer.matchedBuyBox.bedroomRange[1]}`}</span>
                <span>{`Baths: ${buyer.matchedBuyBox.bathroomRange[0]} - ${buyer.matchedBuyBox.bathroomRange[1]}`}</span>
                <span>{`Year built: ${formatYearBuiltRange(
                  buyer.matchedBuyBox.yearBuiltRange,
                )}`}</span>{' '}
                <span>{`Buy with pool: ${
                  buyer.matchedBuyBox.pool === 'no_pool' ? 'No' : 'Yes'
                }`}</span>
                <span>
                  <Collapse>
                    <Collapse.Panel header="Buybox areas" key="1">
                      {buyer.matchedBuyBox.areas.map((area) => (
                        <Tag
                          key={area.formattedAddress}
                          color={
                            area.formattedAddress ===
                            buyer.matchedArea?.formattedAddress
                              ? 'green'
                              : 'default'
                          }
                        >
                          {area.formattedAddress}
                        </Tag>
                      ))}
                    </Collapse.Panel>
                  </Collapse>
                </span>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </>
  );
};

export default BuyboxCheckerPage;
